// BSV CHANCES
import {
  Container,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
} from '@mui/material';
import {useSelector, useDispatch} from 'react-redux';
import React, {useEffect, useState} from 'react';
import {routeStateActions} from '@app/router/redux';
import {navbarComponentName} from '@app/router/redux/routeHandler';
import {useStyles} from './styles';
import {strings} from '@app/common/strings';
import {
  getDistanceRequest,
  postDistanceRequest,
  clearDistanceData,
} from '@app/screens/module-specific-utilities/pages/expense-distance/redux';
import {
  getExpenseDistanceData,
  getExpenseDistanceLoading,
} from './redux/selectors';

export const util: any = {downloadExcel: null};

interface DistanceItem {
  fromStationName: string;
  toStationName: string;
  distance: number;
  ids: number[];
}

export const ExpenseDistance = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [fromStation, setFromStation] = useState('');
  const [toStation, setToStation] = useState('');
  const [results, setResults] = useState<DistanceItem[]>([]);
  const [originalData, setOriginalData] = useState<DistanceItem[]>([]);
  const [distanceInputs, setDistanceInputs] = useState<{[key: number]: string}>(
    {},
  );
  const [fromError, setFromError] = useState(false);
  const [toError, setToError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const data = useSelector(getExpenseDistanceData);
  const expenseDistanceLoading = useSelector(getExpenseDistanceLoading);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const cloned = data.map(item => ({...item, ids: [...item.ids]}));
      setResults(cloned);
      setOriginalData(cloned);

      const inputs: {[key: number]: string} = {};
      cloned.forEach((item, idx) => {
        inputs[idx] = item.distance.toString();
      });
      setDistanceInputs(inputs);
    }
  }, [data]);

  useEffect(() => {
    dispatch(
      routeStateActions.setNavbarComponentName(
        navbarComponentName.commonheader,
      ),
    );
    dispatch(
      routeStateActions.setNavbarComponentHeading(
        <div className={classes.navbarComponentHeadingEmpty}>
          {strings.ModuleSpecificUtilitie.expenseDistancePage.ExpenseDistance}
        </div>
      )
    );
    return () => {
      dispatch(routeStateActions.setNavbarComponentName(null));
      dispatch(clearDistanceData());
    };
  }, []);

  const handleSearch = (): void => {
    const trimmedFrom = fromStation.trim();
    const trimmedTo = toStation.trim();

    const isFromValid = trimmedFrom.length >= 3;
    const isToValid = trimmedTo.length >= 3;

    setFromError(!isFromValid);
    setToError(!isToValid);

    if (!isFromValid || !isToValid) return;

    dispatch(
      getDistanceRequest({fromStation: trimmedFrom, toStation: trimmedTo}),
    );
    setHasSearched(true);
  };

  const handleRefresh = (): void => {
    setFromStation('');
    setToStation('');
    setResults([]);
    setOriginalData([]);
    setDistanceInputs({});
    setFromError(false);
    setToError(false);
    setHasSearched(false);
    dispatch(clearDistanceData());
  };

  const handleDistanceChange = (index: number, value: string): void => {
    setDistanceInputs(prev => ({...prev, [index]: value}));

    const numeric = Number(value);
    if (!isNaN(numeric)) {
      const updated = results.map((item, idx) =>
        idx === index ? {...item, distance: numeric} : item,
      );
      setResults(updated);
    }
  };

  const handleSave = (): void => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmSave = () => {
    setIsDialogOpen(false);

    const changedItems = results.filter((item, index) => {
      const original = originalData[index];
      return original && Number(original.distance) !== Number(item.distance);
    });

    const payload = changedItems.map(item => ({
      ids: item.ids,
      distance: item.distance,
    }));

    if (payload.length > 0) {
      dispatch(postDistanceRequest(payload));

      const updatedResults = results.map((item, index) => {
        const updated = payload.find(
          change => JSON.stringify(change.ids) === JSON.stringify(item.ids),
        );
        return updated ? {...item, distance: updated.distance} : item;
      });

      setResults(updatedResults);
      setOriginalData(updatedResults);

      const newInputs: {[key: number]: string} = {};
      updatedResults.forEach((item, idx) => {
        newInputs[idx] = item.distance.toString();
      });
      setDistanceInputs(newInputs);
    }
  };

  const CircularBar = () => (
    <Box width="100%" display="flex" justifyContent="center">
      <CircularProgress />
    </Box>
  );

  return (
    <div>
      <Container maxWidth={false}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle1" gutterBottom>
              {strings.ModuleSpecificUtilitie.expenseDistance.From}
            </Typography>
            <TextField
              label="From Station Name"
              value={fromStation}
              onChange={e => setFromStation(e.target.value)}
              fullWidth
              error={fromError}
              helperText={fromError ? 'Minimum 3 characters required' : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle1" gutterBottom>
              {strings.ModuleSpecificUtilitie.expenseDistance.To}
            </Typography>
            <TextField
              label="To Station Name"
              value={toStation}
              onChange={e => setToStation(e.target.value)}
              fullWidth
              error={toError}
              helperText={toError ?  `${strings.ModuleSpecificUtilitie.expenseDistance.minChar}` : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={4} style={{marginTop: 30}}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={fromStation.length < 1 || toStation.length < 1}
              style={{marginRight: 8}}>
              {strings.ModuleSpecificUtilitie.expenseDistance.Search}
            </Button>
            <Button variant="outlined" onClick={handleRefresh}>
              {strings.ModuleSpecificUtilitie.expenseDistance.Refresh}
            </Button>
          </Grid>
        </Grid>

        {expenseDistanceLoading ? (
          <CircularBar />
        ) : results.length > 0 ? (
          <Card style={{marginTop: 24}}>
            <CardContent>
              {results.map((item, idx) => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={idx}
                  style={{marginBottom: 16}}>
                  <Grid item xs={4}>
                    <Typography>
                      {item.fromStationName} - {item.toStationName}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      type="text"
                      value={distanceInputs[idx] ?? item.distance.toString()}
                      onChange={e => handleDistanceChange(idx, e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>
                      {strings.ModuleSpecificUtilitie.expenseDistance.km}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
              <Button variant="contained" color="primary" onClick={handleSave}>
                {strings.ModuleSpecificUtilitie.expenseDistance.Save}
              </Button>
            </CardContent>
          </Card>
        ) : (
          hasSearched && (
            <Typography
              variant="body1"
              color="textSecondary"
              style={{marginTop: 24, textAlign: 'center'}}>
              {strings.ModuleSpecificUtilitie.expenseDistance.NoData}
            </Typography>
          )
        )}
      </Container>

      <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {strings.ModuleSpecificUtilitie.expenseDistance.Confirm}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {strings.ModuleSpecificUtilitie.expenseDistance.ConfirmDescription}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box className={classes.dialogActions}>
            <Button
              variant="contained"
              className={classes.paperButton}
              onClick={closeDialog}>
              {strings.ModuleSpecificUtilitie.expenseDistance.Cancel}
            </Button>
            <Button
              variant="contained"
              className={classes.applyButton}
              onClick={handleConfirmSave}>
              {strings.ModuleSpecificUtilitie.expenseDistance.Proceed}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};
